import Axios from "axios";
import { useEffect, FunctionComponent, memo, useState, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

import Page from "constants/page";
import { setAccessToken, getAccessToken } from "utilities/access-token";
import { refreshToken } from "utilities/refresh-token";
import { showServerError } from "utilities/show-server-error";

import { useUser } from "hooks-for/users/use-user";
import { useRolePermissions } from "hooks-for/permissions/use-role-permissions";
import { useClient } from "hooks-for/clients/use-client";

import { authenticate, findRedirectPage } from "./authenticate.utilities";
import { IProps } from "./authenticate.types";
import "./authenticate.scss";
import { setClientId } from "utilities/client-id";

/**
 * This is the "redirect URI" page in the authentication flow. Cognito calls this route with a code that can be used to
 * generate an access token.
 * @returns Essentially blank page for authenticating users.
 */
const Authenticate: FunctionComponent<IProps> = ({ refresh = false }) => {
    const history = useHistory();
    const accessToken = useMemo(() => getAccessToken(), []);
    const location = useLocation();
    const [hasAccessToken, setHasAccessToken] = useState(() => typeof getAccessToken() === "string");

    // Request the user details
    const { data: userDetails, isLoading: isUserLoading } = useUser({ enabled: hasAccessToken });

    // Request the user's client.
    const { data: clientInformation, isLoading: isClientLoading } = useClient(userDetails?.clientId!, {
        enabled: typeof userDetails !== "undefined",
    });

    // Request the user's permissions
    const { data: userPermissions, isLoading: isUserPermissionsLoading } = useRolePermissions(userDetails?.roleId!, {
        enabled: typeof userDetails !== "undefined",
    });

    useEffect(() => {
        const getRefreshToken = async () => {
            try {
                if (typeof accessToken !== "string") {
                    const newToken = await refreshToken();
                    setAccessToken(newToken);
                    setHasAccessToken(true);
                }
            } catch (error) {
                showServerError(error);
            }
        };

        const getAccessToken = async () => {
            try {
                const { accessToken } = await authenticate(location.search);
                setAccessToken(accessToken);
                setHasAccessToken(true);
            } catch (serverError) {
                if (Axios.isAxiosError(serverError)) {
                    showServerError(serverError);
                }

                history.replace(Page.Login);
            } finally {
                localStorage.removeItem("authenticationState");
                localStorage.removeItem("authenticationCodeVerifier");
            }
        };

        if (refresh) {
            getRefreshToken();
        } else {
            getAccessToken();
        }
    }, [accessToken, history, location.search, refresh]);

    useEffect(() => {
        if (!isUserLoading && !isUserPermissionsLoading && !isClientLoading) {
            const accessToken = getAccessToken();

            if (typeof userDetails?.clientId === "string") {
                setClientId(userDetails.clientId);
            }

            if (typeof accessToken === "string") {
                const redirectPage = findRedirectPage(userPermissions, clientInformation);

                history.push(redirectPage);
            }
        }
    }, [
        history,
        isUserLoading,
        isUserPermissionsLoading,
        userPermissions,
        isClientLoading,
        clientInformation,
        userDetails?.clientId,
    ]);

    return <div className="authenticate">Authenticating&hellip;</div>;
};

export default memo(Authenticate);
